/**
 * Canvas side of frames leaving the frames store: drop their nodes and edges,
 * and keep selection and a focus presentation that contained them consistent.
 * All removed ids are reconciled in one write: the frames are already gone,
 * so any later `nodes` write would drop their nodes without this cleanup.
 */
import type { Viewport } from "@xyflow/react";
import {
  conversationCompanionLayoutSurface,
  reconcileConversationCompanionFocusLayout,
} from "@/lib/conversation-companion-focus-geometry";
import { scrubPresentationClasses } from "@/lib/presentation-classes";
import type { CanvasState } from "@/stores/canvas-store";
import type { PresentationState } from "@/lib/focus/focus-types";
import { canvasFocusPresentation } from "@/lib/focus/focus-renderer-kind";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import {
  SELECTED_FRAME_Z_BASE,
  demotedZIndex,
} from "@/stores/canvas-selection-z";
import type { StoreApi } from "zustand";

interface FrameNodeRemoval {
  partial: Partial<CanvasState>;
  presentation: PresentationState | null;
  selectedFrameId: string | null;
  nodeIds: ReadonlySet<string>;
  restoredViewport: Viewport | null;
}

function frameNodeRemoval(
  state: CanvasState,
  presentation: PresentationState | null,
  currentSelectedFrameId: string | null,
  removed: ReadonlySet<string>,
): FrameNodeRemoval {
  const restore = state.presentationRestore;
  const focusPresentation =
    presentation?.variant === "focus" &&
    presentation.frameIds.some((id) => removed.has(id))
      ? presentation
      : null;
  const nextNodeBase = state.nodes.filter((n) => !removed.has(n.id));
  const nextNodeIds = new Set(nextNodeBase.map((node) => node.id));
  const remainingFocusFrameIds =
    focusPresentation?.frameIds.filter(
      (id) => !removed.has(id) && nextNodeIds.has(id),
    ) ?? [];
  const companionLayout =
    focusPresentation?.customFocusLayout?.kind ===
    "conversation_companion_stack"
      ? focusPresentation.customFocusLayout
      : null;
  const removesCompanionAnchor =
    !!companionLayout && removed.has(companionLayout.conversationFrameId);
  const keepsFocusPresentation =
    !!focusPresentation &&
    remainingFocusFrameIds.length > 0 &&
    !removesCompanionAnchor;
  const previousActiveFrameId =
    focusPresentation?.frameIds[focusPresentation.activeIndex] ?? null;
  const preservedFocusActiveIndex = previousActiveFrameId
    ? remainingFocusFrameIds.indexOf(previousActiveFrameId)
    : -1;
  const nextFocusActiveIndex = keepsFocusPresentation
    ? preservedFocusActiveIndex >= 0
      ? preservedFocusActiveIndex
      : Math.min(
          focusPresentation.activeIndex,
          remainingFocusFrameIds.length - 1,
        )
    : -1;
  const remainingFocusFrameIdSet = new Set(remainingFocusFrameIds);
  const canvasFocus = canvasFocusPresentation(focusPresentation);
  const focusHiddenFrameIds =
    canvasFocus && keepsFocusPresentation
      ? new Set(
          nextNodeBase
            .filter((node) => !remainingFocusFrameIdSet.has(node.id))
            .map((node) => node.id),
        )
      : canvasFocus
        ? new Set(
            (restore?.hiddenFrameIds ?? []).filter((id) => nextNodeIds.has(id)),
          )
        : new Set(
            [...state.focusHiddenFrameIds].filter((id) => !removed.has(id)),
          );
  const selectedFrameId = keepsFocusPresentation
    ? (remainingFocusFrameIds[nextFocusActiveIndex] ?? null)
    : currentSelectedFrameId && nextNodeIds.has(currentSelectedFrameId)
      ? currentSelectedFrameId
      : null;
  const nextCompanionLayout =
    keepsFocusPresentation && companionLayout
      ? reconcileConversationCompanionFocusLayout(
          {
            ...companionLayout,
            surface:
              (focusPresentation.focusWindow
                ? undefined
                : focusPresentation.surface) ??
              conversationCompanionLayoutSurface(companionLayout),
          },
          focusPresentation.frameIds,
          remainingFocusFrameIds,
          companionLayout.expandedStackFrameId &&
            !removed.has(companionLayout.expandedStackFrameId) &&
            remainingFocusFrameIds.includes(
              companionLayout.expandedStackFrameId,
            )
            ? companionLayout.expandedStackFrameId
            : remainingFocusFrameIds[remainingFocusFrameIds.length - 1]!,
        )
      : undefined;
  const restoreCompanionSnapshots =
    !!focusPresentation && removesCompanionAnchor;
  const presentationSnapshotById = new Map(
    (restore?.dims ?? []).map((snapshot) => [snapshot.frameId, snapshot]),
  );
  const remainingBoxes = remainingFocusFrameIds.map(
    (id) => focusPresentation?.boxes[focusPresentation.frameIds.indexOf(id)],
  );
  const nextPresentation = keepsFocusPresentation
    ? {
        ...focusPresentation,
        frameIds: remainingFocusFrameIds,
        activeIndex: nextFocusActiveIndex,
        customFocusLayout:
          nextCompanionLayout ?? focusPresentation.customFocusLayout,
        focusWindow:
          focusPresentation.focusWindow &&
          removed.has(focusPresentation.focusWindow.frameId)
            ? undefined
            : focusPresentation.focusWindow,
        boxes: remainingBoxes.every((box) => !!box)
          ? (remainingBoxes as FocusWindowBox[])
          : [],
        naturalSizes: Object.fromEntries(
          Object.entries(focusPresentation.naturalSizes).filter(([id]) =>
            remainingFocusFrameIdSet.has(id),
          ),
        ),
      }
    : focusPresentation
      ? null
      : presentation;
  return {
    presentation: nextPresentation,
    selectedFrameId: restoreCompanionSnapshots ? null : selectedFrameId,
    nodeIds: nextNodeIds,
    restoredViewport:
      restoreCompanionSnapshots && restore ? restore.viewport : null,
    partial: {
      nodes: nextNodeBase.map((node) => {
        const snapshot = restoreCompanionSnapshots
          ? presentationSnapshotById.get(node.id)
          : undefined;
        return {
          ...node,
          ...(snapshot && {
            position: snapshot.position ?? node.position,
            width: snapshot.width,
            height: snapshot.height,
            style: snapshot.style,
            measured: snapshot.measured,
          }),
          className: restoreCompanionSnapshots
            ? (snapshot?.className ?? scrubPresentationClasses(node.className))
            : node.className,
          hidden:
            !!node.data.metadata?.hidden || focusHiddenFrameIds.has(node.id),
          selected: keepsFocusPresentation
            ? node.id === selectedFrameId
            : restoreCompanionSnapshots
              ? false
              : node.selected,
          zIndex:
            keepsFocusPresentation && node.id === selectedFrameId
              ? SELECTED_FRAME_Z_BASE
              : restoreCompanionSnapshots
                ? demotedZIndex(node.zIndex)
                : node.zIndex,
        };
      }),
      edges: state.edges.filter(
        (e) => !removed.has(e.source) && !removed.has(e.target),
      ),
      viewport:
        restoreCompanionSnapshots && restore
          ? restore.viewport
          : state.viewport,
      focusHiddenFrameIds,
      ...(focusPresentation && {
        presentationRestore:
          keepsFocusPresentation && restore
            ? {
                ...restore,
                dims: restore.dims.filter(
                  (item) =>
                    !removed.has(item.frameId) && nextNodeIds.has(item.frameId),
                ),
              }
            : null,
      }),
    },
  };
}

function afterFrameNodeRemoval(
  store: StoreApi<CanvasState>,
  restoredViewport: Viewport | null,
): void {
  const current = store.getState();
  if (restoredViewport) {
    current.setViewportProgrammatic?.(restoredViewport, { duration: 0 });
    void import("@/stores/desktop-mode-store").then(({ useDesktopMode }) =>
      useDesktopMode.getState().clearCompactFrameOverlay(),
    );
  }
  const { presentation } = usePresentationStore.getState();
  if (
    presentation?.variant === "focus" &&
    (presentation.customFocusLayout?.kind === "conversation_companion_stack" ||
      presentation.frameIds.every((id) =>
        current.nodes.some(
          (node) => node.id === id && node.data.frameType === "conversation",
        ),
      ))
  ) {
    void import("@/lib/focus-presentation-reflow").then(
      ({ scheduleFocusPresentationReflow }) =>
        scheduleFocusPresentationReflow(),
    );
  }
}

/** Drop edges and focus hiding of frames that had no node here. */
function removeNodelessFrameReferences(
  state: CanvasState,
  removed: ReadonlySet<string>,
): Partial<CanvasState> | CanvasState {
  const edges = state.edges.filter(
    (edge) => !removed.has(edge.source) && !removed.has(edge.target),
  );
  const focusHidden = [...state.focusHiddenFrameIds].filter(
    (id) => !removed.has(id),
  );
  if (
    edges.length === state.edges.length &&
    focusHidden.length === state.focusHiddenFrameIds.size
  ) {
    return state;
  }
  return { edges, focusHiddenFrameIds: new Set(focusHidden) };
}

/** Remove the nodes of frames that left the frames store, all in one write. */
export function removeFrameNodes(
  store: StoreApi<CanvasState>,
  frameIds: readonly string[],
): void {
  const removed = new Set(frameIds);
  if (!store.getState().nodes.some((node) => removed.has(node.id))) {
    useSelectionStore.getState().retain((id) => !removed.has(id));
    store.setState((state) => removeNodelessFrameReferences(state, removed));
    return;
  }
  const { presentation, setPresentation } = usePresentationStore.getState();
  const removal = frameNodeRemoval(
    store.getState(),
    presentation,
    useSelectionStore.getState().selectedFrameId,
    removed,
  );
  // The presentation and selection land first, so a mirror's focus guard
  // never keeps the removed frames' nodes.
  if (removal.presentation !== presentation) {
    setPresentation(removal.presentation);
  }
  useSelectionStore.setState((selection) => ({
    selectedFrameId: removal.selectedFrameId,
    multiSelectedFrameIds: selection.multiSelectedFrameIds.filter((id) =>
      removal.nodeIds.has(id),
    ),
  }));
  store.setState(removal.partial);
  afterFrameNodeRemoval(store, removal.restoredViewport);
}
